import { formatMoney } from "../../lib/money";
/* Shared sales-order stage metadata for the Orders workspace (store side) and
 * the Sales CRM drawer. Pure data — the backend stage keys are the source of
 * truth (server.py sales-order flow); nothing here invents a new stage. */

export const SO_STAGES = [
  { key: "draft_order",      label: "Orders",         short: "Draft",      tone: "#8b5cf6", pct: 10 },
  { key: "sales_review",     label: "Confirmed",      short: "Review",     tone: "#3b82f6", pct: 25 },
  { key: "awaiting_deposit", label: "Waiting Deposit", short: "Deposit",   tone: "#f59e0b", pct: 40 },
  { key: "in_production",    label: "Production",     short: "Production", tone: "#7c3aed", pct: 65 },
  { key: "second_payment",   label: "Second Payment", short: "Balance",    tone: "#ec4899", pct: 80 },
  { key: "ready_to_ship",    label: "Ready to Ship",  short: "Ready",      tone: "#22c55e", pct: 100 },
];
export const SO_DECLINED = { key: "declined", label: "Declined", tone: "#ef4444", pct: 0 };

export const stageIndex = (status) => SO_STAGES.findIndex((s) => s.key === status);
export const stageOf = (status) =>
  SO_STAGES.find((s) => s.key === status) || (status === "declined" ? SO_DECLINED : SO_STAGES[0]);
export const progressOf = (order) => stageOf(order?.status).pct;

/* Friendly copy for stage_history entries ({from, to, by, at, note}). */
export const TIMELINE_LABELS = {
  draft_order:      "Order created",
  sales_review:     "Sent to Sales team",
  awaiting_deposit: "Approved by Sales — deposit requested",
  in_production:    "Deposit verified — production started",
  second_payment:   "Second payment requested",
  ready_to_ship:    "Balance verified — ready to ship",
  declined:         "Order declined",
};

/* Chronological timeline: stage moves + payment-proof uploads + quote origin. */
export function timelineOf(order) {
  if (!order) return [];
  const ev = [];
  if (order.quote_number) {
    ev.push({ at: order.created_at, label: `Created from quote ${order.quote_number}`, kind: "origin" });
  }
  for (const h of order.stage_history || []) {
    ev.push({
      at: h.at, kind: h.to === "declined" ? "declined" : "stage", stage: h.to,
      label: TIMELINE_LABELS[h.to] || (h.to || "").replace(/_/g, " "),
      note: h.note,
    });
  }
  for (const p of order.deposit_proofs || []) {
    ev.push({ at: p.at, kind: "upload", label: `Deposit proof uploaded — ${p.filename || "file"}`, url: p.url });
  }
  for (const p of order.second_payment_proofs || []) {
    ev.push({ at: p.at, kind: "upload", label: `Balance proof uploaded — ${p.filename || "file"}`, url: p.url });
  }
  return ev.filter((e) => e.at).sort((a, b) => String(a.at).localeCompare(String(b.at)));
}

/* Estimated delivery — a display heuristic only (no backend field): 30 days
 * from entering production, else 45 days from creation. */
export function estDeliveryOf(order) {
  if (!order || order.status === "declined") return null;
  if (order.status === "ready_to_ship") return null;
  const base = order.ts_in_production || order.created_at;
  if (!base) return null;
  const d = new Date(base);
  if (isNaN(d)) return null;
  d.setDate(d.getDate() + (order.ts_in_production ? 30 : 45));
  return d;
}

export const soMoney = (n) =>
  formatMoney(n);

export const soDate = (d) => {
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

export const soAgo = (ts) => {
  const t = new Date(ts).getTime();
  if (!t || isNaN(t)) return "";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return soDate(ts);
};

/* Per-thread read markers (client-side; the backend has no read state). */
const readKey = (id) => `lhc-chat-read:${id}`;
export const lastReadOf = (id) => { try { return localStorage.getItem(readKey(id)) || ""; } catch { return ""; } };
export const markThreadRead = (id) => { try { localStorage.setItem(readKey(id), new Date().toISOString()); } catch { /* no-op */ } };

export function unreadOf(messages, meId, threadId) {
  const since = lastReadOf(threadId);
  return (messages || []).filter((m) => m.author_id !== meId && (!since || String(m.created_at) > since)).length;
}
